/* GanzaX storefront behaviour: product stage views, 3D canvas, room scale, finish preview, sticky buy bar.
   No dependencies. Everything degrades to the static page if this file never runs. */
(() => {
  const reduceMotion = matchMedia('(prefers-reduced-motion: reduce)');
  const finePointer = matchMedia('(hover: hover) and (pointer: fine)');
  // The finish radios carry data-gx-finish="wrap|white"; their name and value are localised for the order.
  const isFinish = (el) => el instanceof HTMLInputElement && el.dataset.gxFinish != null;

  /* ---------- 3D canvas: drag to turn, lean toward a fine pointer ---------- */
  function initCanvas(root) {
    if (root.dataset.ready) return;
    root.dataset.ready = '1';
    const obj = root.querySelector('.gx-c3d__obj');
    const base = { x: 8, y: -34 };
    const state = { x: base.x, y: base.y, tx: base.x, ty: base.y, raf: 0 };
    const apply = () => {
      obj.style.setProperty('--gx-rx', `${state.x.toFixed(2)}deg`);
      obj.style.setProperty('--gx-ry', `${state.y.toFixed(2)}deg`);
    };
    const tick = () => {
      state.x += (state.tx - state.x) * 0.14;
      state.y += (state.ty - state.y) * 0.14;
      apply();
      if (Math.abs(state.tx - state.x) + Math.abs(state.ty - state.y) > 0.05) {
        state.raf = requestAnimationFrame(tick);
      } else {
        state.raf = 0;
      }
    };
    const target = (x, y) => {
      state.tx = Math.max(-28, Math.min(28, x));
      state.ty = Math.max(-62, Math.min(62, y));
      if (reduceMotion.matches) {
        state.x = state.tx;
        state.y = state.ty;
        apply();
      } else if (!state.raf) {
        state.raf = requestAnimationFrame(tick);
      }
    };
    apply();

    let drag = null;
    root.addEventListener('pointerdown', (event) => {
      if (event.button !== 0) return;
      drag = { id: event.pointerId, x: event.clientX, y: event.clientY, rx: state.tx, ry: state.ty, moved: false };
      root.setPointerCapture(event.pointerId);
      root.classList.add('is-dragging');
    });
    root.addEventListener('pointermove', (event) => {
      if (drag && event.pointerId === drag.id) {
        const dx = event.clientX - drag.x;
        const dy = event.clientY - drag.y;
        if (Math.abs(dx) > 3) drag.moved = true;
        target(drag.rx - dy * 0.15, drag.ry + dx * 0.35);
      } else if (!drag && finePointer.matches && !reduceMotion.matches) {
        const r = root.getBoundingClientRect();
        const px = (event.clientX - r.left) / r.width - 0.5;
        const py = (event.clientY - r.top) / r.height - 0.5;
        target(base.x - py * 10, base.y + px * 22);
      }
    });
    let dragged = false;
    const end = (event) => {
      if (!drag || event.pointerId !== drag.id) return;
      dragged = drag.moved;
      drag = null;
      root.classList.remove('is-dragging');
    };
    // A drag that turned the canvas is not a click on the link around it.
    root.addEventListener('click', (event) => {
      if (dragged) {
        event.preventDefault();
        event.stopPropagation();
        dragged = false;
      }
    }, true);
    root.addEventListener('pointerup', end);
    root.addEventListener('pointercancel', end);
    root.addEventListener('lostpointercapture', end);
    root.addEventListener('pointerleave', () => {
      if (!drag && finePointer.matches) target(base.x, base.y);
    });
  }

  /* ---------- Product stage: photograph / 3D / on the wall ---------- */
  function showView(stage, view, focusTab) {
    stage.querySelectorAll('[data-view]').forEach((panel) => {
      panel.hidden = panel.dataset.view !== view;
    });
    stage.querySelectorAll('[data-view-tab]').forEach((tab) => {
      const on = tab.dataset.viewTab === view;
      tab.setAttribute('aria-selected', String(on));
      tab.tabIndex = on ? 0 : -1;
      if (on && focusTab) tab.focus();
    });
    const hint = stage.querySelector('[data-gx-hint="3d"]');
    if (hint) hint.hidden = view !== '3d';
  }

  function sizeRoom(room, value) {
    const m = /(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/i.exec(value || '');
    if (!m) return;
    const a = Number(m[1]);
    const b = Number(m[2]);
    const short = Math.min(a, b);
    const long = Math.max(a, b);
    const portrait = room.closest('[data-gx-stage]')?.dataset.portrait === 'true';
    room.style.setProperty('--gx-w', portrait ? short : long);
    room.style.setProperty('--gx-h', portrait ? long : short);
    const caption = room.parentElement.querySelector('[data-gx-room-caption] bdi');
    if (caption) caption.textContent = value.trim();
    room.dataset.size = value;
  }

  /* A real room photograph: the print is placed at true scale from the scene's own measurements. */
  function sizePhotoRoom(room, value) {
    const m = /(\d+(?:\.\d+)?)\s*[×x]\s*(\d+(?:\.\d+)?)/i.exec(value || '');
    if (!m) return;
    room.dataset.size = value;
    const portrait = room.closest('[data-gx-stage]')?.dataset.portrait === 'true';
    const a = Number(m[1]);
    const b = Number(m[2]);
    const wCm = portrait ? Math.min(a, b) : Math.max(a, b);
    const hCm = portrait ? Math.max(a, b) : Math.min(a, b);
    const W = Number(room.dataset.w);
    const H = Number(room.dataset.h);
    const k = Number(room.dataset.k);
    const pw = wCm * k;
    const ph = hCm * k;
    const top = Number(room.dataset.back) - 20 * k - ph; // hung 20 cm above the sofa back
    // A tall print in a low photograph: the wall continues above the photograph instead of cropping the print.
    const extra = Math.max(0, H * 0.06 - top);
    const total = H + extra;
    room.style.aspectRatio = `${W} / ${total}`;
    room.classList.toggle('is-extended', extra > 0);
    const print = room.querySelector('.gx-photo-room__print');
    print.style.left = `${((Number(room.dataset.cx) - pw / 2) / W) * 100}%`;
    print.style.top = `${((top + extra) / total) * 100}%`;
    print.style.width = `${(pw / W) * 100}%`;
    print.style.height = `${(ph / total) * 100}%`;
  }

  function sizeAllRooms(scope, value) {
    scope.querySelectorAll('[data-gx-room]').forEach((room) => sizeRoom(room, value ?? room.dataset.size));
    scope.querySelectorAll('[data-gx-photo-room]').forEach((room) => sizePhotoRoom(room, value ?? room.dataset.size));
  }

  function showScene(stage, scene) {
    stage.querySelectorAll('[data-gx-photo-room]').forEach((room) => (room.hidden = room.dataset.scene !== scene));
    stage.querySelector('[data-gx-room]').hidden = scene !== 'colour';
    stage.querySelector('[data-gx-walls]').hidden = scene !== 'colour';
    stage.querySelectorAll('[data-gx-scene]').forEach((b) => b.setAttribute('aria-pressed', String(b.dataset.gxScene === scene)));
  }

  function initStage(stage) {
    stage.querySelectorAll('[data-gx-c3d]').forEach(initCanvas);
    sizeAllRooms(stage);
    stage.querySelectorAll('[data-gx-scene]').forEach((b) => b.addEventListener('click', () => showScene(stage, b.dataset.gxScene)));
    stage.querySelectorAll('[data-gx-wall]').forEach((b) => b.addEventListener('click', () => {
      stage.querySelector('[data-gx-room]').style.setProperty('--gx-wall', b.dataset.gxWall);
      stage.querySelectorAll('[data-gx-wall]').forEach((x) => x.setAttribute('aria-pressed', String(x === b)));
    }));

    const tabs = [...stage.querySelectorAll('[data-view-tab]')];
    tabs.forEach((tab, i) => {
      tab.addEventListener('click', () => showView(stage, tab.dataset.viewTab, false));
      tab.addEventListener('keydown', (event) => {
        const rtl = getComputedStyle(stage).direction === 'rtl';
        const next = { ArrowLeft: rtl ? 1 : -1, ArrowRight: rtl ? -1 : 1, Home: -i, End: tabs.length - 1 - i }[event.key];
        if (next === undefined) return;
        event.preventDefault();
        const to = tabs[(i + next + tabs.length) % tabs.length];
        showView(stage, to.dataset.viewTab, true);
      });
    });

    const dialog = stage.querySelector('[data-gx-lightbox]');
    stage.querySelector('[data-gx-zoom]')?.addEventListener('click', () => dialog?.showModal());
    dialog?.addEventListener('click', (event) => {
      if (event.target === dialog || event.target.closest('[data-gx-close]')) dialog.close();
    });
  }

  /* ---------- Finish choice and size choice drive the previews ---------- */
  document.addEventListener('change', (event) => {
    const input = event.target;
    if (!(input instanceof HTMLInputElement)) return;
    if (isFinish(input)) {
      const finish = input.dataset.gxFinish;
      document.querySelectorAll('[data-gx-stage]').forEach((stage) => {
        stage.querySelectorAll('[data-gx-c3d]').forEach((c) => (c.dataset.finish = finish));
        showView(stage, '3d', false);
      });
      document.querySelectorAll('.gx-edge-finish').forEach((f) => f.classList.remove('is-missing'));
    } else if (input.type === 'radio' && input.closest('variant-picker')) {
      sizeAllRooms(document, input.value);
    }
  });

  // The native required check on the finish radios: mark the group so the inline message shows.
  document.addEventListener('invalid', (event) => {
    if (isFinish(event.target)) event.target.closest('.gx-edge-finish')?.classList.add('is-missing');
  }, true);

  /* ---------- Sticky buy bar on phones: the action is always one tap away ---------- */
  function initBuyBar(bar) {
    const form = bar.closest('product-form-component') || document.querySelector('product-form-component');
    const main = form?.querySelector('button[name="add"]');
    if (!main) return;
    const priceTarget = bar.querySelector('[data-gx-buybar-price]');
    const priceSource = document.querySelector('.product-details product-price, .product-details .price');
    const syncPrice = () => {
      if (priceSource && priceTarget) priceTarget.textContent = priceSource.textContent.trim();
    };
    syncPrice();
    if (priceSource) new MutationObserver(syncPrice).observe(priceSource, { subtree: true, childList: true, characterData: true });

    new IntersectionObserver(([entry]) => {
      bar.classList.toggle('is-visible', !entry.isIntersecting);
    }).observe(main);

    bar.querySelector('[data-gx-buybar-add]')?.addEventListener('click', () => {
      const chosen = form.querySelector('input[data-gx-finish]:checked');
      if (!chosen) {
        const field = form.querySelector('.gx-edge-finish');
        field?.classList.add('is-missing');
        field?.scrollIntoView({ behavior: reduceMotion.matches ? 'auto' : 'smooth', block: 'center' });
        field?.querySelector('input')?.focus({ preventScroll: true });
        return;
      }
      main.click();
    });
  }

  const init = (scope = document) => {
    scope.querySelectorAll('[data-gx-stage]').forEach(initStage);
    scope.querySelectorAll('[data-gx-c3d]').forEach(initCanvas);
    scope.querySelectorAll('[data-gx-buybar]').forEach(initBuyBar);
  };
  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', () => init());
  else init();
  document.addEventListener('shopify:section:load', (event) => init(event.target));
})();
